<template>
  <div class="crud-boletos">
    <!-- ── HEADER ── -->
    <div class="page-header">
      <div>
        <h1 class="page-title-wrap">Boletos</h1>
        <p class="page-sub">Tipos de boleto y compras del evento activo</p>
      </div>
      <button class="btn-primary" @click="abrirCrear">
        <i class="bi bi-plus-lg"></i> Nuevo tipo de boleto
      </button>
    </div>

    <!-- ── CARDS RESUMEN ── -->
    <div class="resumen-grid" v-if="resumen">
      <div class="resumen-card">
        <div class="resumen-label">Total recaudado</div>
        <div class="resumen-val resumen-gold">
          {{ formatMXN(resumen.total_recaudado) }}
        </div>
        <div class="resumen-sub">{{ resumen.compras_pagadas }} compras pagadas</div>
      </div>
      <div class="resumen-card">
        <div class="resumen-label">Boletos vendidos</div>
        <div class="resumen-val">{{ resumen.boletos_vendidos }}</div>
        <div class="resumen-sub">de {{ resumen.boletos_cupo_total }} disponibles</div>
        <div class="cupo-track" v-if="resumen.boletos_cupo_total">
          <div
            class="cupo-fill"
            :style="{ width: pctCupo + '%' }"
            :class="cupoClass"
          ></div>
        </div>
      </div>
      <div class="resumen-card">
        <div class="resumen-label">Compras pendientes</div>
        <div class="resumen-val resumen-warn">{{ resumen.compras_pendientes }}</div>
        <div class="resumen-sub">Esperando pago de Stripe</div>
      </div>
      <div class="resumen-card">
        <div class="resumen-label">Cancelados</div>
        <div class="resumen-val resumen-danger">{{ resumen.compras_canceladas }}</div>
        <div class="resumen-sub">compras canceladas</div>
      </div>
    </div>

    <!-- ── TABS PRINCIPALES ── -->
    <div class="main-tabs">
      <button
        :class="['main-tab', { activo: tabActivo === 'tipos' }]"
        @click="tabActivo = 'tipos'"
      >
        <i class="bi bi-ticket-perforated-fill"></i> Tipos de boleto
        <span class="tab-count">{{ tipos.length }}</span>
      </button>
      <button
        :class="['main-tab', { activo: tabActivo === 'compras' }]"
        @click="tabActivo = 'compras'"
      >
        <i class="bi bi-bag-check-fill"></i> Compras
        <span class="tab-count">{{ compras.length }}</span>
      </button>
      <button
        :class="['main-tab', { activo: tabActivo === 'fisicos' }]"
        @click="tabActivo = 'fisicos'"
      >
        <i class="bi bi-qr-code"></i> Boletos físicos
        <span class="tab-count">{{ lotesFisicos.length }}</span>
      </button>
    </div>

    <!-- ══════════════════════════════════════
         TAB: TIPOS DE BOLETO
    ══════════════════════════════════════ -->
    <div v-if="tabActivo === 'tipos'">
      <div v-if="cargandoTipos" class="estado-centro">
        <span class="spinner-border spinner-border-sm text-warning"></span> Cargando…
      </div>
      <div v-else-if="!tipos.length" class="estado-centro">
        <i class="bi bi-ticket-perforated" style="font-size: 2rem; opacity: 0.25"></i>
        <p>Sin tipos de boleto registrados. Crea el primero.</p>
      </div>
      <div v-else class="tipos-grid">
        <div
          v-for="t in tipos"
          :key="t.id"
          :class="['tipo-card', { inactivo: !t.activo }]"
        >
          <div class="tipo-header">
            <div class="tipo-badges">
              <span :class="['badge-tipo', t.tipo]">{{ t.tipo_display }}</span>
              <span v-if="t.destacado" class="badge-destacado">
                <i class="bi bi-star-fill"></i> Destacado
              </span>
            </div>
            <div class="tipo-acciones">
              <button
                :class="['btn-toggle', t.activo ? 'activo' : 'inactivo']"
                :title="t.activo ? 'Desactivar' : 'Activar'"
                @click="toggleActivo(t)"
              >
                <i :class="t.activo ? 'bi bi-eye-fill' : 'bi bi-eye-slash-fill'"></i>
              </button>
              <button class="btn-icon" title="Editar" @click="abrirEditar(t)">
                <i class="bi bi-pencil-fill"></i>
              </button>
              <button
                class="btn-icon btn-danger-ghost"
                title="Eliminar"
                @click="eliminarTipo(t)"
              >
                <i class="bi bi-trash3"></i>
              </button>
            </div>
          </div>

          <p class="tipo-nombre">{{ t.nombre }}</p>
          <p class="tipo-desc" v-if="t.descripcion">{{ t.descripcion }}</p>
          <p v-if="t.mensaje_promocional" class="tipo-promo">
            <i class="bi bi-megaphone-fill"></i> {{ t.mensaje_promocional }}
          </p>

          <!-- Beneficios -->
          <div v-if="t.beneficios?.length" class="beneficios-list">
            <span v-for="b in t.beneficios" :key="b.id" class="badge-beneficio">
              <i class="bi bi-check2"></i> {{ b.nombre }}
            </span>
          </div>

          <div class="tipo-stats">
            <div class="tipo-stat">
              <span class="stat-label">Precio</span>
              <span class="stat-val gold">{{ formatMXN(t.precio) }}</span>
            </div>
            <div class="tipo-stat">
              <span class="stat-label">Cupo</span>
              <span class="stat-val">{{ t.cupo ?? "∞" }}</span>
            </div>
            <div class="tipo-stat">
              <span class="stat-label">Vendidos</span>
              <span class="stat-val">{{ t.boletos_vendidos }}</span>
            </div>
            <div class="tipo-stat">
              <span class="stat-label">Orden</span>
              <span class="stat-val">{{ t.orden }}</span>
            </div>
          </div>

          <!-- Mini barra de cupo si tiene límite (0 cuenta como límite: agotado) -->
          <div class="cupo-wrap" v-if="t.cupo !== null && t.cupo !== undefined">
            <div class="cupo-track">
              <div
                class="cupo-fill"
                :style="{
                  width: Math.min(100, t.cupo ? (t.boletos_vendidos / t.cupo) * 100 : 100) + '%',
                }"
                :class="cupoClassTipo(t)"
              ></div>
            </div>
            <span class="cupo-txt">
              {{ t.cupo ? Math.min(100, Math.round((t.boletos_vendidos / t.cupo) * 100)) : 100 }}%
            </span>
          </div>
        </div>
      </div>
    </div>

    <!-- ══════════════════════════════════════
         TAB: COMPRAS
    ══════════════════════════════════════ -->
    <div v-if="tabActivo === 'compras'">
      <!-- Filtros compras -->
      <div class="filtros-bar">
        <div class="search-wrap">
          <i class="bi bi-search search-ico"></i>
          <input
            v-model="busquedaCompras"
            class="search-input"
            placeholder="Nombre, apellido o email del comprador…"
            @input="onBusquedaCompras"
          />
          <button v-if="busquedaCompras" class="search-clear" @click="limpiarBusqueda">
            <i class="bi bi-x"></i>
          </button>
        </div>
        <div class="tab-filtros">
          <button
            v-for="f in filtrosEstatus"
            :key="f.value"
            :class="['tab-filt', { activo: filtroStatusPago === f.value }]"
            @click="seleccionarFiltroCompra(f.value)"
          >
            <span :class="['dot-estatus', f.value || 'todos']"></span>
            {{ f.label }}
          </button>
        </div>
      </div>

      <div class="tabla-wrapper">
        <div v-if="cargandoCompras" class="estado-centro">
          <span class="spinner-border spinner-border-sm text-warning"></span> Cargando…
        </div>
        <div v-else-if="!comprasPagina.length" class="estado-centro">
          <i class="bi bi-bag-x" style="font-size: 2rem; opacity: 0.25"></i>
          <p>Sin compras para los filtros seleccionados.</p>
        </div>
        <table v-else class="tabla">
          <thead>
            <tr>
              <th @click="toggleSortCompras('comprador_apellido')" class="th-sortable">
                Comprador <i :class="sortIconCompras('comprador_apellido')"></i>
              </th>
              <th @click="toggleSortCompras('tipo_boleto_nombre')" class="th-sortable">
                Tipo <i :class="sortIconCompras('tipo_boleto_nombre')"></i>
              </th>
              <th @click="toggleSortCompras('cantidad')" class="th-sortable">
                Cant. <i :class="sortIconCompras('cantidad')"></i>
              </th>
              <th @click="toggleSortCompras('total_pagado')" class="th-sortable">
                Total <i :class="sortIconCompras('total_pagado')"></i>
              </th>
              <th>Asignaciones</th>
              <th @click="toggleSortCompras('status_pago')" class="th-sortable">
                Estatus <i :class="sortIconCompras('status_pago')"></i>
              </th>
              <th @click="toggleSortCompras('fecha_compra')" class="th-sortable">
                Fecha <i :class="sortIconCompras('fecha_compra')"></i>
              </th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="c in comprasPagina" :key="c.id" class="tabla-fila">
              <td>
                <span v-if="c.es_fisico" class="badge-fisico">
                  <i class="bi bi-qr-code"></i> Venta física (taquilla)
                </span>
                <template v-else>
                  <p class="nombre">{{ c.comprador_nombre }} {{ c.comprador_apellido }}</p>
                  <p class="equipo">{{ c.comprador_email }}</p>
                </template>
              </td>
              <td>
                <span :class="['badge-tipo', c.tipo_boleto_tipo]">{{
                  c.tipo_boleto_nombre
                }}</span>
              </td>
              <td>{{ c.cantidad }}</td>
              <td class="gold-text">{{ formatMXN(c.total_pagado) }}</td>
              <td>
                <span class="asig-count">
                  {{ c.asignaciones_confirmadas }}/{{ c.asignaciones_total }}
                  <i class="bi bi-person-check-fill"></i>
                </span>
              </td>
              <td>
                <span :class="['badge-estatus', c.status_pago]">
                  {{ c.status_pago_display }}
                </span>
              </td>
              <td class="fecha-cell">{{ formatFechaCorta(c.fecha_compra) }}</td>
              <td>
                <button
                  class="btn-icon"
                  title="Ver detalle"
                  @click="abrirDetalleCompra(c)"
                >
                  <i class="bi bi-eye-fill"></i>
                </button>
              </td>
            </tr>
          </tbody>
        </table>

        <!-- Paginación compras -->
        <div v-if="totalPaginasCompras > 1" class="paginacion">
          <button
            class="pag-btn"
            :disabled="paginaCompras === 1"
            @click="paginaCompras--"
          >
            <i class="bi bi-chevron-left"></i>
          </button>
          <template v-for="n in paginasVisiblesCompras" :key="n">
            <span v-if="n === '...'" class="pag-ellipsis">…</span>
            <button
              v-else
              :class="['pag-btn', { 'pag-activa': n === paginaCompras }]"
              @click="paginaCompras = n"
            >
              {{ n }}
            </button>
          </template>
          <button
            class="pag-btn"
            :disabled="paginaCompras === totalPaginasCompras"
            @click="paginaCompras++"
          >
            <i class="bi bi-chevron-right"></i>
          </button>
          <span class="pag-info">
            {{ (paginaCompras - 1) * porPagina + 1 }}–{{
              Math.min(paginaCompras * porPagina, comprasOrdenadas.length)
            }}
            de {{ comprasOrdenadas.length }}
          </span>
        </div>
      </div>
    </div>

    <!-- ══════════════════════════════════════
         TAB: BOLETOS FÍSICOS (VENTA EN TAQUILLA)
    ══════════════════════════════════════ -->
    <div v-if="tabActivo === 'fisicos'">
      <p class="fisicos-intro">
        Genera un lote de boletos físicos (con QR) para venta en efectivo el día del
        evento. Los números se reservan de inmediato y ya no estarán disponibles para
        la venta en línea.
      </p>

      <div v-if="!tiposConVentaFisica.length" class="estado-centro">
        <i class="bi bi-qr-code" style="font-size: 2rem; opacity: 0.25"></i>
        <p>
          Ningún tipo de boleto tiene habilitada la venta física. Actívala desde
          "Editar" en la pestaña de Tipos de boleto.
        </p>
      </div>

      <div v-else class="tipos-grid">
        <div v-for="t in tiposConVentaFisica" :key="t.id" class="tipo-card">
          <div class="tipo-header">
            <div class="tipo-badges">
              <span :class="['badge-tipo', t.tipo]">{{ t.tipo_display }}</span>
            </div>
          </div>
          <p class="tipo-nombre">{{ t.nombre }}</p>

          <div class="tipo-stats">
            <div class="tipo-stat">
              <span class="stat-label">Vendidos</span>
              <span class="stat-val">{{ t.boletos_vendidos }}</span>
            </div>
            <div class="tipo-stat">
              <span class="stat-label">Cupo</span>
              <span class="stat-val">{{ t.cupo ?? "∞" }}</span>
            </div>
            <div class="tipo-stat">
              <span class="stat-label">Disponibles</span>
              <span class="stat-val">{{
                t.cupo !== null ? t.cupo - t.boletos_vendidos : "∞"
              }}</span>
            </div>
          </div>

          <button
            class="btn-primary btn-generar-fisicos"
            :disabled="t.cupo !== null && t.cupo - t.boletos_vendidos <= 0"
            @click="abrirModalGenerar(t)"
          >
            <i class="bi bi-qr-code-scan"></i> Generar boletos
          </button>
        </div>
      </div>

      <!-- Historial de lotes -->
      <div class="lotes-section">
        <h3 class="lotes-titulo">Lotes generados</h3>
        <div v-if="cargandoLotes" class="estado-centro">
          <span class="spinner-border spinner-border-sm text-warning"></span> Cargando…
        </div>
        <div v-else-if="!lotesFisicos.length" class="estado-centro">
          <p>Aún no se ha generado ningún lote de boletos físicos.</p>
        </div>
        <table v-else class="tabla">
          <thead>
            <tr>
              <th>Tipo</th>
              <th>Cantidad</th>
              <th>Rango de números</th>
              <th>Fecha</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="lote in lotesFisicos" :key="lote.id" class="tabla-fila">
              <td>{{ lote.tipo_boleto_nombre }}</td>
              <td>{{ lote.cantidad }}</td>
              <td>#{{ lote.numero_inicio }} – #{{ lote.numero_fin }}</td>
              <td class="fecha-cell">{{ formatFechaCorta(lote.fecha_compra) }}</td>
              <td>
                <button
                  class="btn-icon"
                  title="Volver a descargar PDF"
                  :disabled="descargandoLoteId === lote.id"
                  @click="descargarLote(lote)"
                >
                  <span v-if="descargandoLoteId === lote.id" class="spinner-border spinner-border-sm"></span>
                  <i v-else class="bi bi-file-earmark-pdf-fill"></i>
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- ═══════════════════════════════════════════════════════
         MODAL — GENERAR BOLETOS FÍSICOS
    ══════════════════════════════════════════════════════ -->
    <div v-if="modalGenerar" class="modal-overlay" @click.self="cerrarModalGenerar">
      <div class="modal-panel modal-mediano">
        <div class="modal-header">
          <div>
            <h2 class="modal-titulo">Generar boletos físicos</h2>
            <p class="modal-sub" v-if="tipoGenerar">{{ tipoGenerar.nombre }}</p>
          </div>
          <button class="btn-icon" @click="cerrarModalGenerar">
            <i class="bi bi-x-lg"></i>
          </button>
        </div>

        <div class="form-grid" v-if="tipoGenerar">
          <div class="form-group col-span-2">
            <label>Cantidad a generar *</label>
            <input
              v-model.number="cantidadGenerar"
              type="number"
              min="1"
              :max="disponiblesGenerar"
              class="input-form"
              placeholder="Ej. 100"
            />
          </div>

          <div class="form-group col-span-2" v-if="cantidadGenerar > 0">
            <p class="preview-rango">
              Se generarán los boletos
              <strong>#{{ tipoGenerar.boletos_vendidos + 1 }}</strong>
              al
              <strong>#{{ tipoGenerar.boletos_vendidos + cantidadGenerar }}</strong>
              ({{ cantidadGenerar }} boletos)
            </p>
            <p v-if="disponiblesGenerar !== null && cantidadGenerar > disponiblesGenerar" class="preview-error">
              Solo quedan {{ disponiblesGenerar }} boletos disponibles en este tipo.
            </p>
          </div>
        </div>

        <div class="modal-footer">
          <button class="btn-secondary" @click="cerrarModalGenerar" :disabled="generandoLote">
            Cancelar
          </button>
          <button
            class="btn-primary"
            :disabled="!cantidadGenerarValida || generandoLote"
            @click="generarLote"
          >
            <span v-if="generandoLote"
              ><span class="spinner-border spinner-border-sm"></span> Generando…</span
            >
            <span v-else><i class="bi bi-qr-code-scan"></i> Generar y descargar PDF</span>
          </button>
        </div>
      </div>
    </div>

    <!-- ═══════════════════════════════════════════════════════
         MODAL — CREAR / EDITAR TIPO DE BOLETO
    ══════════════════════════════════════════════════════ -->
    <div v-if="modalTipo" class="modal-overlay" @click.self="cerrarModalTipo">
      <div class="modal-panel modal-mediano">
        <div class="modal-header">
          <div>
            <h2 class="modal-titulo">
              {{ modoEdicion ? "Editar tipo de boleto" : "Nuevo tipo de boleto" }}
            </h2>
            <p class="modal-sub" v-if="modoEdicion">{{ tipoForm.nombre }}</p>
          </div>
          <button class="btn-icon" @click="cerrarModalTipo">
            <i class="bi bi-x-lg"></i>
          </button>
        </div>

        <div class="form-grid" v-if="tipoForm">
          <div class="form-group col-span-2">
            <label>Nombre *</label>
            <input
              v-model="tipoForm.nombre"
              class="input-form"
              placeholder="Ej. Boleto VIP, General…"
            />
          </div>

          <div class="form-group">
            <label>Tipo *</label>
            <select v-model="tipoForm.tipo" class="input-form">
              <option value="">-- Selecciona --</option>
              <option v-for="t in tiposBoleto" :key="t.value" :value="t.value">
                {{ t.label }}
              </option>
            </select>
          </div>

          <div class="form-group">
            <label>Precio (MXN) *</label>
            <input
              v-model="tipoForm.precio"
              type="number"
              step="0.01"
              min="0"
              class="input-form"
              placeholder="0.00"
            />
          </div>

          <div class="form-group">
            <label>Cupo <span class="label-hint">(vacío = sin límite, 0 = agotado)</span></label>
            <input
              v-model="tipoForm.cupo"
              type="number"
              min="0"
              class="input-form"
              placeholder="200"
            />
          </div>

          <div class="form-group">
            <label>Orden de aparición</label>
            <input
              v-model="tipoForm.orden"
              type="number"
              min="0"
              class="input-form"
              placeholder="0"
            />
          </div>

          <div class="form-group col-span-2">
            <label>Descripción</label>
            <textarea
              v-model="tipoForm.descripcion"
              class="input-form"
              rows="3"
              placeholder="Describe qué incluye este boleto…"
            ></textarea>
          </div>

          <div class="form-group col-span-2">
            <label
              >Mensaje promocional
              <span class="label-hint">(máx. 50 caracteres)</span></label
            >
            <input
              v-model="tipoForm.mensaje_promocional"
              class="input-form"
              maxlength="50"
              placeholder="Ej. ¡Últimos lugares!"
            />
          </div>

          <div class="form-group checks-row col-span-2">
            <label class="check-label">
              <input type="checkbox" v-model="tipoForm.activo" class="check-input" />
              <span>Activo <span class="label-hint">(visible en la página)</span></span>
            </label>
            <label class="check-label">
              <input type="checkbox" v-model="tipoForm.destacado" class="check-input" />
              <span>Destacado <span class="label-hint">(aparece resaltado)</span></span>
            </label>
            <label class="check-label">
              <input type="checkbox" v-model="tipoForm.permite_venta_fisica" class="check-input" />
              <span>Permite venta física <span class="label-hint">(taquilla, efectivo)</span></span>
            </label>
          </div>

          <div class="form-group col-span-2" v-if="tipoForm.permite_venta_fisica">
            <label>Plantilla del boleto físico *</label>
            <select v-model="tipoForm.plantilla_fisica" class="input-form">
              <option value="">-- Selecciona --</option>
              <option value="general">General</option>
              <option value="ringside">Ringside</option>
            </select>
          </div>

          <!-- Beneficios -->
          <div class="form-group col-span-2">
            <label>
              Beneficios incluidos
              <span class="label-hint"> — selecciona los que aplican</span>
            </label>
            <div v-if="cargandoBeneficios" class="beneficios-loading">
              <span class="spinner-border spinner-border-sm text-warning"></span>
              Cargando…
            </div>
            <div v-else-if="!catalogoBeneficios.length" class="beneficios-vacio">
              Sin beneficios en el catálogo.
              <button type="button" class="btn-link" @click="crearBeneficioInline">
                + Crear uno
              </button>
            </div>
            <div v-else class="beneficios-checks">
              <label
                v-for="b in catalogoBeneficios"
                :key="b.id"
                class="beneficio-check-label"
              >
                <input
                  type="checkbox"
                  :value="b.id"
                  v-model="tipoForm.beneficios_ids"
                  class="check-input"
                />
                <span>{{ b.nombre }}</span>
              </label>
            </div>
            <!-- Crear beneficio nuevo desde el modal -->
            <div class="nuevo-beneficio-row" v-if="catalogoBeneficios.length">
              <input
                v-model="nuevoBeneficioNombre"
                class="input-form input-nuevo-ben"
                placeholder="Nuevo beneficio…"
                @keydown.enter.prevent="guardarNuevoBeneficio"
              />
              <button
                type="button"
                class="btn-add-ben"
                :disabled="!nuevoBeneficioNombre.trim()"
                @click="guardarNuevoBeneficio"
              >
                <i class="bi bi-plus-lg"></i>
              </button>
            </div>
          </div>
        </div>

        <div class="modal-footer">
          <button class="btn-secondary" @click="cerrarModalTipo" :disabled="guardando">
            Cancelar
          </button>
          <button
            class="btn-primary"
            :disabled="guardando || !tipoFormValido"
            @click="guardarTipo"
          >
            <span v-if="guardando"
              ><span class="spinner-border spinner-border-sm"></span
            ></span>
            <span v-else
              ><i class="bi bi-floppy-fill"></i>
              {{ modoEdicion ? "Guardar cambios" : "Crear boleto" }}</span
            >
          </button>
        </div>
      </div>
    </div>

    <!-- ═══════════════════════════════════════════════════════
         MODAL — DETALLE DE COMPRA + ASIGNACIONES
    ══════════════════════════════════════════════════════ -->
    <div v-if="modalCompra" class="modal-overlay" @click.self="cerrarDetalleCompra">
      <div class="modal-panel modal-detalle">
        <div class="modal-header">
          <div>
            <h2 class="modal-titulo">Detalle de compra #{{ compraActiva?.id }}</h2>
            <p class="modal-sub" v-if="compraActiva">
              {{ compraActiva.comprador_nombre }} {{ compraActiva.comprador_apellido }} ·
              {{ compraActiva.comprador_email }}
            </p>
          </div>
          <button class="btn-icon" @click="cerrarDetalleCompra">
            <i class="bi bi-x-lg"></i>
          </button>
        </div>

        <div class="detalle-compra-body" v-if="compraActiva">
          <!-- Resumen de la compra -->
          <div class="compra-resumen-grid">
            <div class="detalle-item">
              <span class="detalle-label">Tipo de boleto</span>
              <span :class="['badge-tipo', compraActiva.tipo_boleto_tipo]">
                {{ compraActiva.tipo_boleto_nombre }}
              </span>
            </div>
            <div class="detalle-item">
              <span class="detalle-label">Cantidad</span>
              <span class="detalle-val">{{ compraActiva.cantidad }}</span>
            </div>
            <div class="detalle-item">
              <span class="detalle-label">Total pagado</span>
              <span class="detalle-val gold-text">{{
                formatMXN(compraActiva.total_pagado)
              }}</span>
            </div>
            <div class="detalle-item">
              <span class="detalle-label">Estatus</span>
              <span :class="['badge-estatus', compraActiva.status_pago]">
                {{ compraActiva.status_pago_display }}
              </span>
            </div>
            <div class="detalle-item">
              <span class="detalle-label">Teléfono</span>
              <span class="detalle-val">{{
                compraActiva.comprador_telefono || "—"
              }}</span>
            </div>
            <div class="detalle-item">
              <span class="detalle-label">Fecha de compra</span>
              <span class="detalle-val">{{
                formatFechaLarga(compraActiva.fecha_compra)
              }}</span>
            </div>
          </div>

          <!-- Asignaciones -->
          <div class="asig-section">
            <div class="asig-header">
              <span class="asig-titulo">
                <i class="bi bi-person-vcard-fill"></i>
                Asignaciones de boleto
              </span>
              <span class="asig-badge">
                {{ compraActiva.asignaciones_confirmadas }} confirmadas /
                {{ compraActiva.asignaciones_total }} total
              </span>
            </div>

            <div v-if="!compraActiva.asignaciones?.length" class="asig-vacio">
              Sin asignaciones generadas aún.
            </div>

            <div v-else class="asig-lista">
              <div
                v-for="a in compraActiva.asignaciones"
                :key="a.id"
                :class="['asig-item', { confirmada: a.confirmado }]"
              >
                <div class="asig-info">
                  <span class="asig-nombre">
                    {{ a.nombre_asistente || "Sin registrar" }}
                    <span v-if="a.es_menor" class="badge-menor">Menor</span>
                  </span>
                  <span class="asig-email">{{ a.email_asistente || "—" }}</span>
                </div>
                <div class="asig-estado">
                  <span v-if="a.confirmado" class="estado-confirmado">
                    <i class="bi bi-check-circle-fill"></i> Confirmado
                  </span>
                  <span v-else class="estado-pendiente">
                    <i class="bi bi-hourglass-split"></i> Pendiente
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="modal-footer">
          <button class="btn-secondary" @click="cerrarDetalleCompra">Cerrar</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from "vue";
import Swal from "sweetalert2";
import {
  getResumenBoletosDashboard,
  getTiposBoletosDashboard,
  crearTipoBoletoDashboard,
  editarTipoBoletoDashboard,
  eliminarTipoBoletoDashboard,
  getComprasDashboard,
  getCatalogoBeneficios,
  crearBeneficio,
  getLotesFisicosDashboard,
  generarBoletosFisicos,
  redescargarPdfLote,
} from "@/utils/api_dashboard";

// ── Catálogos ─────────────────────────────────────────────────────────────────
const tiposBoleto = [
  { value: "general", label: "General" },
  { value: "primera_fila", label: "Primera Fila" },
  { value: "mesa_vip", label: "Mesa VIP" },
  { value: "streaming", label: "Streaming" },
];

const filtrosEstatus = [
  { value: "", label: "Todos" },
  { value: "pagado", label: "Pagados" },
  { value: "pendiente", label: "Pendientes" },
  { value: "procesando", label: "Procesando" },
  { value: "cancelado", label: "Cancelados" },
];

// ── Estado ────────────────────────────────────────────────────────────────────
const tabActivo = ref("tipos");
const resumen = ref(null);
const tipos = ref([]);
const compras = ref([]);
const cargandoTipos = ref(false);
const cargandoCompras = ref(false);
const guardando = ref(false);

// Compras — filtros y orden
const busquedaCompras = ref("");
const filtroStatusPago = ref("");
const sortColCompras = ref("fecha_compra");
const sortDirCompras = ref("desc");
const paginaCompras = ref(1);
const porPagina = ref(12);

// Modales
const modalTipo = ref(false);
const modoEdicion = ref(false);
const tipoForm = ref(null);
const tipoEditandoId = ref(null);

// Beneficios — catálogo y creación inline
const catalogoBeneficios = ref([]);
const cargandoBeneficios = ref(false);
const nuevoBeneficioNombre = ref("");

const modalCompra = ref(false);
const compraActiva = ref(null);

// Boletos físicos
const lotesFisicos = ref([]);
const cargandoLotes = ref(false);
const lotesCargados = ref(false);
const modalGenerar = ref(false);
const tipoGenerar = ref(null);
const cantidadGenerar = ref(null);
const generandoLote = ref(false);
const descargandoLoteId = ref(null);

// ── Carga ─────────────────────────────────────────────────────────────────────
async function cargarResumen() {
  try {
    resumen.value = await getResumenBoletosDashboard();
  } catch {
    /* silencioso — no bloquea el resto */
  }
}

async function cargarTipos() {
  cargandoTipos.value = true;
  try {
    tipos.value = await getTiposBoletosDashboard();
  } catch {
    swalError("No se pudieron cargar los tipos de boleto.");
  } finally {
    cargandoTipos.value = false;
  }
}

async function cargarCompras() {
  paginaCompras.value = 1;
  cargandoCompras.value = true;
  try {
    const params = {};
    if (filtroStatusPago.value) params.status_pago = filtroStatusPago.value;
    if (busquedaCompras.value.trim()) params.q = busquedaCompras.value.trim();
    compras.value = await getComprasDashboard(params);
  } catch {
    swalError("No se pudieron cargar las compras.");
  } finally {
    cargandoCompras.value = false;
  }
}

onMounted(async () => {
  await Promise.all([cargarResumen(), cargarTipos(), cargarCatalogoBeneficios()]);
  // Compras se carga al cambiar de tab para no saturar
});

// Cargar compras al entrar a ese tab por primera vez
const comprasCargadas = ref(false);
function onTabCompras() {
  if (!comprasCargadas.value) {
    comprasCargadas.value = true;
    cargarCompras();
  }
}

async function cargarLotes() {
  cargandoLotes.value = true;
  try {
    lotesFisicos.value = await getLotesFisicosDashboard();
  } catch {
    swalError("No se pudieron cargar los lotes de boletos físicos.");
  } finally {
    cargandoLotes.value = false;
  }
}
function onTabFisicos() {
  if (!lotesCargados.value) {
    lotesCargados.value = true;
    cargarLotes();
  }
}

// Watcher de tab
import { watch } from "vue";
watch(tabActivo, (val) => {
  if (val === "compras") onTabCompras();
  if (val === "fisicos") onTabFisicos();
});

// ── Resumen helpers ───────────────────────────────────────────────────────────
const pctCupo = computed(() => {
  if (!resumen.value?.boletos_cupo_total) return 0;
  return Math.min(
    100,
    Math.round((resumen.value.boletos_vendidos / resumen.value.boletos_cupo_total) * 100)
  );
});
const cupoClass = computed(() => {
  if (pctCupo.value >= 90) return "cupo-rojo";
  if (pctCupo.value >= 60) return "cupo-amarillo";
  return "cupo-verde";
});
function cupoClassTipo(t) {
  if (t.cupo === null || t.cupo === undefined) return "cupo-verde";
  if (t.cupo === 0) return "cupo-rojo";
  const p = (t.boletos_vendidos / t.cupo) * 100;
  if (p >= 90) return "cupo-rojo";
  if (p >= 60) return "cupo-amarillo";
  return "cupo-verde";
}

// ── Boletos físicos ───────────────────────────────────────────────────────────
const tiposConVentaFisica = computed(() =>
  tipos.value.filter((t) => t.permite_venta_fisica)
);

const disponiblesGenerar = computed(() => {
  if (!tipoGenerar.value || tipoGenerar.value.cupo === null || tipoGenerar.value.cupo === undefined)
    return null;
  return tipoGenerar.value.cupo - tipoGenerar.value.boletos_vendidos;
});

const cantidadGenerarValida = computed(() => {
  if (!cantidadGenerar.value || cantidadGenerar.value < 1) return false;
  if (disponiblesGenerar.value !== null && cantidadGenerar.value > disponiblesGenerar.value)
    return false;
  return true;
});

function abrirModalGenerar(t) {
  tipoGenerar.value = t;
  cantidadGenerar.value = null;
  modalGenerar.value = true;
}
function cerrarModalGenerar() {
  modalGenerar.value = false;
  tipoGenerar.value = null;
}

function descargarBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

// Cuando el backend responde con un error y pedimos responseType: 'blob',
// axios no puede parsear el JSON automáticamente — el body llega como Blob.
async function extraerErrorBlob(e) {
  const data = e.response?.data;
  if (data instanceof Blob) {
    try {
      const texto = await data.text();
      return JSON.parse(texto)?.detail;
    } catch {
      return null;
    }
  }
  return data?.detail;
}

async function generarLote() {
  if (!cantidadGenerarValida.value) return;
  generandoLote.value = true;
  try {
    const blob = await generarBoletosFisicos(tipoGenerar.value.id, cantidadGenerar.value);
    const inicio = tipoGenerar.value.boletos_vendidos + 1;
    const fin = tipoGenerar.value.boletos_vendidos + cantidadGenerar.value;
    descargarBlob(blob, `boletos_${tipoGenerar.value.nombre.toLowerCase().replace(/\s+/g, "_")}_${inicio}-${fin}.pdf`);

    await Promise.all([cargarTipos(), cargarLotes(), cargarResumen()]);
    cerrarModalGenerar();
    swalToast("success", `${cantidadGenerar.value} boletos generados (#${inicio}-#${fin})`);
  } catch (e) {
    swalError((await extraerErrorBlob(e)) || "No se pudo generar el lote de boletos.");
  } finally {
    generandoLote.value = false;
  }
}

async function descargarLote(lote) {
  descargandoLoteId.value = lote.id;
  try {
    const blob = await redescargarPdfLote(lote.id);
    descargarBlob(blob, `boletos_${lote.tipo_boleto_nombre.toLowerCase().replace(/\s+/g, "_")}_lote${lote.id}.pdf`);
  } catch {
    swalError("No se pudo descargar el PDF de este lote.");
  } finally {
    descargandoLoteId.value = null;
  }
}

async function cargarCatalogoBeneficios() {
  cargandoBeneficios.value = true;
  try {
    catalogoBeneficios.value = await getCatalogoBeneficios();
  } catch {
    /* silencioso */
  } finally {
    cargandoBeneficios.value = false;
  }
}

// ── CRUD Tipos ────────────────────────────────────────────────────────────────
function tipoFormVacio() {
  return {
    nombre: "",
    tipo: "",
    descripcion: "",
    precio: "",
    cupo: null,
    orden: 0,
    activo: true,
    destacado: false,
    mensaje_promocional: "",
    permite_venta_fisica: false,
    plantilla_fisica: "",
    beneficios_ids: [],
  };
}

const tipoFormValido = computed(
  () =>
    tipoForm.value?.nombre?.trim() &&
    tipoForm.value?.tipo &&
    tipoForm.value?.precio !== "" &&
    tipoForm.value?.precio >= 0 &&
    (!tipoForm.value?.permite_venta_fisica || tipoForm.value?.plantilla_fisica)
);

function abrirCrear() {
  modoEdicion.value = false;
  tipoEditandoId.value = null;
  tipoForm.value = tipoFormVacio();
  modalTipo.value = true;
}

function abrirEditar(t) {
  modoEdicion.value = true;
  tipoEditandoId.value = t.id;
  tipoForm.value = {
    nombre: t.nombre,
    tipo: t.tipo,
    descripcion: t.descripcion || "",
    precio: t.precio,
    cupo: t.cupo ?? null,
    orden: t.orden,
    activo: t.activo,
    destacado: t.destacado,
    mensaje_promocional: t.mensaje_promocional || "",
    permite_venta_fisica: t.permite_venta_fisica || false,
    plantilla_fisica: t.plantilla_fisica || "",
    beneficios_ids: (t.beneficios || []).map((b) => b.id),
  };
  modalTipo.value = true;
}

function cerrarModalTipo() {
  modalTipo.value = false;
  tipoForm.value = null;
}

async function guardarTipo() {
  if (!tipoFormValido.value) return;
  guardando.value = true;
  try {
    // ⚠️ No usar truthy check aquí: 0 es un cupo válido (boleto agotado).
    // `tipoForm.value.cupo ? ... : null` convertía 0 en null (sin límite),
    // lo que dejaba comprar boletos aunque el cupo real fuera cero.
    const cupoVacio = tipoForm.value.cupo === "" || tipoForm.value.cupo === null;
    const payload = {
      ...tipoForm.value,
      precio: parseFloat(tipoForm.value.precio),
      cupo: cupoVacio ? null : parseInt(tipoForm.value.cupo),
      orden: parseInt(tipoForm.value.orden) || 0,
    };
    if (modoEdicion.value) {
      const actualizado = await editarTipoBoletoDashboard(tipoEditandoId.value, payload);
      const idx = tipos.value.findIndex((t) => t.id === tipoEditandoId.value);
      if (idx !== -1) tipos.value[idx] = actualizado;
    } else {
      const nuevo = await crearTipoBoletoDashboard(payload);
      tipos.value.push(nuevo);
    }
    await cargarResumen();
    cerrarModalTipo();
    swalToast(
      "success",
      modoEdicion.value ? "Tipo de boleto actualizado" : "Tipo de boleto creado"
    );
  } catch (e) {
    swalError(e.response?.data ? JSON.stringify(e.response.data) : "Error al guardar.");
  } finally {
    guardando.value = false;
  }
}

async function toggleActivo(t) {
  try {
    const actualizado = await editarTipoBoletoDashboard(t.id, { activo: !t.activo });
    const idx = tipos.value.findIndex((x) => x.id === t.id);
    if (idx !== -1) tipos.value[idx] = actualizado;
    swalToast("success", `Tipo ${actualizado.activo ? "activado" : "desactivado"}`);
  } catch {
    swalError("No se pudo cambiar el estado.");
  }
}

async function eliminarTipo(t) {
  const confirm = await Swal.fire({
    title: `¿Eliminar "${t.nombre}"?`,
    text: t.boletos_vendidos
      ? "Este tipo tiene boletos vendidos y no puede eliminarse."
      : "Esta acción no se puede deshacer.",
    icon: t.boletos_vendidos ? "warning" : "question",
    showCancelButton: !t.boletos_vendidos,
    confirmButtonText: t.boletos_vendidos ? "Entendido" : "Sí, eliminar",
    cancelButtonText: "Cancelar",
    background: "#0f1520",
    color: "#fff",
    confirmButtonColor: "#f3bc2c",
  });
  if (!confirm.isConfirmed || t.boletos_vendidos) return;

  try {
    await eliminarTipoBoletoDashboard(t.id);
    tipos.value = tipos.value.filter((x) => x.id !== t.id);
    swalToast("success", "Tipo de boleto eliminado");
  } catch (e) {
    const msg = e.response?.data?.detail || "No se pudo eliminar.";
    swalError(msg);
  }
}

// ── Compras — ordenamiento y paginación ──────────────────────────────────────
function toggleSortCompras(col) {
  if (sortColCompras.value === col) {
    sortDirCompras.value = sortDirCompras.value === "asc" ? "desc" : "asc";
  } else {
    sortColCompras.value = col;
    sortDirCompras.value = col === "fecha_compra" ? "desc" : "asc";
  }
  paginaCompras.value = 1;
}
function sortIconCompras(col) {
  if (sortColCompras.value !== col) return "bi bi-arrow-down-up sort-neutral";
  return sortDirCompras.value === "asc"
    ? "bi bi-arrow-up sort-active"
    : "bi bi-arrow-down sort-active";
}

const comprasOrdenadas = computed(() => {
  const col = sortColCompras.value;
  const dir = sortDirCompras.value === "asc" ? 1 : -1;
  return [...compras.value].sort((a, b) => {
    const va = (a[col] ?? "").toString().toLowerCase();
    const vb = (b[col] ?? "").toString().toLowerCase();
    if (va < vb) return -1 * dir;
    if (va > vb) return 1 * dir;
    return 0;
  });
});

const totalPaginasCompras = computed(() =>
  Math.max(1, Math.ceil(comprasOrdenadas.value.length / porPagina.value))
);
const comprasPagina = computed(() => {
  const ini = (paginaCompras.value - 1) * porPagina.value;
  return comprasOrdenadas.value.slice(ini, ini + porPagina.value);
});
const paginasVisiblesCompras = computed(() => {
  const total = totalPaginasCompras.value;
  const cur = paginaCompras.value;
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  const pages = [1];
  if (cur > 3) pages.push("...");
  for (let i = Math.max(2, cur - 1); i <= Math.min(total - 1, cur + 1); i++)
    pages.push(i);
  if (cur < total - 2) pages.push("...");
  pages.push(total);
  return pages;
});

let busquedaTimer = null;
function onBusquedaCompras() {
  clearTimeout(busquedaTimer);
  busquedaTimer = setTimeout(cargarCompras, 350);
}
function limpiarBusqueda() {
  busquedaCompras.value = "";
  cargarCompras();
}
function seleccionarFiltroCompra(val) {
  filtroStatusPago.value = val;
  cargarCompras();
}

// ── Detalle compra ────────────────────────────────────────────────────────────
function abrirDetalleCompra(c) {
  compraActiva.value = c;
  modalCompra.value = true;
}
function cerrarDetalleCompra() {
  modalCompra.value = false;
  compraActiva.value = null;
}

async function guardarNuevoBeneficio() {
  const nombre = nuevoBeneficioNombre.value.trim();
  if (!nombre) return;
  try {
    const nuevo = await crearBeneficio({ nombre });
    catalogoBeneficios.value.push(nuevo);
    catalogoBeneficios.value.sort((a, b) => a.nombre.localeCompare(b.nombre));
    // Auto-seleccionar el recién creado
    tipoForm.value.beneficios_ids.push(nuevo.id);
    nuevoBeneficioNombre.value = "";
    swalToast("success", `Beneficio "${nuevo.nombre}" creado`);
  } catch {
    swalError("No se pudo crear el beneficio.");
  }
}

async function crearBeneficioInline() {
  const { value: nombre } = await Swal.fire({
    title: "Nuevo beneficio",
    input: "text",
    inputPlaceholder: "Ej. Acceso a zona VIP",
    showCancelButton: true,
    confirmButtonText: "Crear",
    cancelButtonText: "Cancelar",
    background: "#0f1520",
    color: "#fff",
    confirmButtonColor: "#f3bc2c",
  });
  if (!nombre?.trim()) return;
  nuevoBeneficioNombre.value = nombre.trim();
  await guardarNuevoBeneficio();
}

// ── Helpers UI ────────────────────────────────────────────────────────────────
function formatMXN(val) {
  return new Intl.NumberFormat("es-MX", { style: "currency", currency: "MXN" }).format(
    val || 0
  );
}
function formatFechaCorta(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  const m = [
    "ene",
    "feb",
    "mar",
    "abr",
    "may",
    "jun",
    "jul",
    "ago",
    "sep",
    "oct",
    "nov",
    "dic",
  ];
  return `${d.getDate()} ${m[d.getMonth()]} ${d.getFullYear()}`;
}
function formatFechaLarga(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  const m = [
    "ene",
    "feb",
    "mar",
    "abr",
    "may",
    "jun",
    "jul",
    "ago",
    "sep",
    "oct",
    "nov",
    "dic",
  ];
  return `${d.getDate()} ${m[d.getMonth()]} ${d.getFullYear()}, ${d.getHours()}:${String(
    d.getMinutes()
  ).padStart(2, "0")}`;
}
function swalToast(icon, title) {
  Swal.fire({
    icon,
    title,
    toast: true,
    position: "top-end",
    showConfirmButton: false,
    timer: 2500,
    background: "#0f1520",
    color: "#fff",
  });
}
function swalError(text) {
  Swal.fire({
    icon: "error",
    title: "Error",
    text,
    background: "#0f1520",
    color: "#fff",
    confirmButtonColor: "#f3bc2c",
  });
}
</script>

<style scoped>
.crud-boletos {
  padding: 32px;
  font-family: "Inter", sans-serif;
  color: #fff;
  min-height: 100%;
}

/* ── Header ── */
.page-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: 24px;
  flex-wrap: wrap;
  gap: 16px;
}
.page-title-wrap {
  color: #f3bc2c;
  font-size: 22px;
  font-weight: 700;
  margin: 0;
}
.page-sub {
  font-size: 12px;
  color: rgba(255, 255, 255, 0.45);
  margin: 4px 0 0;
}

/* ── Cards resumen ── */
.resumen-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 14px;
  margin-bottom: 24px;
}
.resumen-card {
  background: #060d1a;
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 12px;
  padding: 18px 20px;
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.resumen-label {
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.07em;
  color: rgba(255, 255, 255, 0.35);
}
.resumen-val {
  font-size: 28px;
  font-weight: 800;
  line-height: 1.1;
  color: #fff;
}
.resumen-gold {
  color: #f3bc2c;
}
.resumen-warn {
  color: #facc15;
}
.resumen-danger {
  color: #f87171;
}
.resumen-sub {
  font-size: 11px;
  color: rgba(255, 255, 255, 0.35);
  margin-top: 2px;
}

/* ── Tabs principales ── */
.main-tabs {
  display: flex;
  gap: 4px;
  margin-bottom: 16px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.07);
  padding-bottom: 0;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;
}
.main-tabs::-webkit-scrollbar {
  display: none;
}
.main-tab {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  background: transparent;
  border: none;
  border-bottom: 2px solid transparent;
  color: rgba(255, 255, 255, 0.45);
  padding: 10px 16px;
  margin-bottom: -1px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.15s;
}
.main-tab:hover {
  color: #fff;
}
.main-tab.activo {
  color: #f3bc2c;
  border-bottom-color: #f3bc2c;
  font-weight: 600;
}
.tab-count {
  background: rgba(255, 255, 255, 0.08);
  border-radius: 10px;
  padding: 1px 7px;
  font-size: 11px;
  font-weight: 600;
}
.main-tab.activo .tab-count {
  background: rgba(243, 188, 44, 0.15);
}

/* ── Grid tipos de boleto ── */
.tipos-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 16px;
}
.tipo-card {
  background: #060d1a;
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 14px;
  padding: 18px 20px;
  display: flex;
  flex-direction: column;
  gap: 10px;
  transition: border-color 0.15s;
}
.tipo-card:hover {
  border-color: rgba(255, 255, 255, 0.15);
}
.tipo-card.inactivo {
  opacity: 0.5;
}

.tipo-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 8px;
}
.tipo-badges {
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
}
.tipo-acciones {
  display: flex;
  gap: 4px;
  flex-shrink: 0;
}

.badge-tipo {
  border-radius: 20px;
  padding: 2px 10px;
  font-size: 11px;
  font-weight: 600;
  white-space: nowrap;
}
.badge-tipo.general {
  background: rgba(34, 197, 94, 0.12);
  color: #4ade80;
  border: 1px solid rgba(34, 197, 94, 0.25);
}
.badge-tipo.primera_fila {
  background: rgba(59, 130, 246, 0.15);
  color: #60a5fa;
  border: 1px solid rgba(59, 130, 246, 0.3);
}
.badge-tipo.mesa_vip {
  background: rgba(243, 188, 44, 0.15);
  color: #f3bc2c;
  border: 1px solid rgba(243, 188, 44, 0.3);
}
.badge-tipo.streaming {
  background: rgba(168, 85, 247, 0.15);
  color: #c084fc;
  border: 1px solid rgba(168, 85, 247, 0.3);
}

.badge-destacado {
  background: rgba(243, 188, 44, 0.1);
  color: #f3bc2c;
  border: 1px solid rgba(243, 188, 44, 0.2);
  border-radius: 20px;
  padding: 2px 9px;
  font-size: 11px;
  font-weight: 600;
}

.tipo-nombre {
  font-size: 15px;
  font-weight: 700;
  margin: 0;
}
.tipo-desc {
  font-size: 12px;
  color: rgba(255, 255, 255, 0.5);
  margin: 0;
  line-height: 1.5;
}
.tipo-promo {
  font-size: 11px;
  color: #facc15;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 5px;
}

.tipo-stats {
  display: grid;
  grid-template-columns: 1fr 1fr 1fr 1fr;
  gap: 0;
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 8px;
  overflow: hidden;
}
.tipo-stat {
  padding: 8px 6px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
  border-right: 1px solid rgba(255, 255, 255, 0.06);
}
.tipo-stat:last-child {
  border-right: none;
}
.stat-label {
  font-size: 9px;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: rgba(255, 255, 255, 0.3);
}
.stat-val {
  font-size: 13px;
  font-weight: 700;
  color: #fff;
}
.stat-val.gold {
  color: #f3bc2c;
}

.cupo-wrap {
  display: flex;
  align-items: center;
  gap: 8px;
}
.cupo-txt {
  font-size: 11px;
  color: rgba(255, 255, 255, 0.4);
  flex-shrink: 0;
}

/* ── Barra de cupo (compartida) ── */
.cupo-track {
  flex: 1;
  height: 5px;
  background: rgba(255, 255, 255, 0.07);
  border-radius: 3px;
  overflow: hidden;
}
.cupo-fill {
  height: 100%;
  border-radius: 3px;
  transition: width 0.4s;
}
.cupo-verde {
  background: linear-gradient(90deg, #22c55e, #4ade80);
}
.cupo-amarillo {
  background: linear-gradient(90deg, #f59e0b, #fbbf24);
}
.cupo-rojo {
  background: linear-gradient(90deg, #ef4444, #f87171);
}

/* ── Toggle activo ── */
.btn-toggle {
  background: transparent;
  border: 1px solid;
  border-radius: 6px;
  padding: 3px 7px;
  font-size: 12px;
  cursor: pointer;
  transition: all 0.15s;
}
.btn-toggle.activo {
  border-color: rgba(74, 222, 128, 0.3);
  color: #4ade80;
}
.btn-toggle.inactivo {
  border-color: rgba(255, 255, 255, 0.15);
  color: rgba(255, 255, 255, 0.3);
}
.btn-toggle:hover {
  opacity: 0.8;
}

/* ── Filtros compras ── */
.filtros-bar {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 14px;
  flex-wrap: wrap;
}
.search-wrap {
  position: relative;
  flex: 1;
  min-width: 200px;
  max-width: 340px;
}
.search-ico {
  position: absolute;
  left: 11px;
  top: 50%;
  transform: translateY(-50%);
  color: rgba(255, 255, 255, 0.3);
  font-size: 13px;
  pointer-events: none;
}
.search-input {
  width: 100%;
  background: #060d1a;
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 8px;
  color: #fff;
  padding: 8px 32px;
  font-size: 13px;
  outline: none;
  transition: border-color 0.15s;
  box-sizing: border-box;
}
.search-input:focus {
  border-color: rgba(243, 188, 44, 0.4);
}
.search-input::placeholder {
  color: rgba(255, 255, 255, 0.25);
}
.search-clear {
  position: absolute;
  right: 8px;
  top: 50%;
  transform: translateY(-50%);
  background: transparent;
  border: none;
  color: rgba(255, 255, 255, 0.35);
  cursor: pointer;
  font-size: 14px;
}
.search-clear:hover {
  color: #fff;
}
.tab-filtros {
  display: flex;
  gap: 4px;
  flex-wrap: wrap;
}
.tab-filt {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: transparent;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  color: rgba(255, 255, 255, 0.5);
  padding: 6px 12px;
  font-size: 12px;
  cursor: pointer;
  transition: all 0.15s;
}
.tab-filt:hover {
  border-color: rgba(255, 255, 255, 0.3);
  color: #fff;
}
.tab-filt.activo {
  border-color: #f3bc2c;
  color: #f3bc2c;
  background: rgba(243, 188, 44, 0.08);
}
.dot-estatus {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  flex-shrink: 0;
}
.dot-estatus.todos {
  background: rgba(255, 255, 255, 0.4);
}
.dot-estatus.pagado {
  background: #4ade80;
}
.dot-estatus.pendiente {
  background: #facc15;
}
.dot-estatus.procesando {
  background: #60a5fa;
}
.dot-estatus.cancelado {
  background: #f87171;
}

/* ── Tabla compras ── */
.tabla-wrapper {
  background: #060d1a;
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 12px;
  /* ✅ Responsive: antes "overflow: hidden" recortaba la tabla en pantallas
     angostas. Ahora deja hacer scroll horizontal dentro de la card en vez
     de aplastar las columnas o cortar contenido. */
  overflow-x: auto;
  overflow-y: hidden;
  -webkit-overflow-scrolling: touch;
}
.estado-centro {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  gap: 10px;
  color: rgba(255, 255, 255, 0.4);
  font-size: 14px;
}
.tabla {
  width: 100%;
  min-width: 760px;
  border-collapse: collapse;
  font-size: 13px;
}
.tabla thead tr {
  background: rgba(255, 255, 255, 0.04);
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
}
.tabla th {
  padding: 12px 14px;
  text-align: left;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: rgba(255, 255, 255, 0.45);
  white-space: nowrap;
}
.th-sortable {
  cursor: pointer;
  user-select: none;
  transition: color 0.15s;
}
.th-sortable:hover {
  color: #f3bc2c;
}
.sort-neutral {
  opacity: 0.3;
  font-size: 10px;
  margin-left: 3px;
}
.sort-active {
  color: #f3bc2c;
  font-size: 10px;
  margin-left: 3px;
}
.tabla-fila {
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
  transition: background 0.15s;
}
.tabla-fila:hover {
  background: rgba(255, 255, 255, 0.03);
}
.tabla td {
  padding: 12px 14px;
  vertical-align: middle;
}

.nombre {
  font-weight: 600;
  margin: 0;
  font-size: 13px;
}
.equipo {
  margin: 0;
  font-size: 11px;
  color: rgba(255, 255, 255, 0.4);
}
.gold-text {
  color: #f3bc2c;
  font-weight: 600;
}
.fecha-cell {
  color: rgba(255, 255, 255, 0.6);
  font-size: 12px;
  white-space: nowrap;
}

.asig-count {
  font-size: 12px;
  color: rgba(255, 255, 255, 0.6);
  display: inline-flex;
  align-items: center;
  gap: 5px;
}

.badge-estatus {
  border-radius: 20px;
  padding: 3px 10px;
  font-size: 11px;
  font-weight: 600;
  white-space: nowrap;
}
.badge-estatus.pagado {
  background: rgba(34, 197, 94, 0.15);
  color: #4ade80;
}
.badge-estatus.pendiente {
  background: rgba(234, 179, 8, 0.15);
  color: #facc15;
}
.badge-estatus.procesando {
  background: rgba(59, 130, 246, 0.15);
  color: #60a5fa;
}
.badge-estatus.cancelado {
  background: rgba(239, 68, 68, 0.15);
  color: #f87171;
}
.badge-estatus.preregistro {
  background: rgba(255, 255, 255, 0.08);
  color: rgba(255, 255, 255, 0.5);
}

/* Paginación */
.paginacion {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 14px 16px;
  border-top: 1px solid rgba(255, 255, 255, 0.06);
  flex-wrap: wrap;
}
.pag-btn {
  min-width: 32px;
  height: 32px;
  padding: 0 8px;
  background: transparent;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 7px;
  color: rgba(255, 255, 255, 0.6);
  font-size: 12px;
  cursor: pointer;
  transition: all 0.15s;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}
.pag-btn:hover:not(:disabled) {
  border-color: #f3bc2c;
  color: #f3bc2c;
  background: rgba(243, 188, 44, 0.08);
}
.pag-btn:disabled {
  opacity: 0.3;
  cursor: not-allowed;
}
.pag-activa {
  background: #f3bc2c !important;
  color: #0f172a !important;
  border-color: #f3bc2c !important;
  font-weight: 700;
}
.pag-ellipsis {
  color: rgba(255, 255, 255, 0.3);
  font-size: 13px;
  padding: 0 4px;
}
.pag-info {
  margin-left: 8px;
  font-size: 11px;
  color: rgba(255, 255, 255, 0.35);
}

/* ── Botones ── */
.btn-primary {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  background: #f3bc2c;
  color: #0f172a;
  border: none;
  border-radius: 8px;
  padding: 9px 16px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s;
}
.btn-primary:hover:not(:disabled) {
  background: #e5ae24;
}
.btn-primary:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}
.btn-secondary {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  background: transparent;
  color: #fff;
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 8px;
  padding: 9px 16px;
  font-size: 13px;
  cursor: pointer;
  transition: all 0.15s;
}
.btn-secondary:hover:not(:disabled) {
  border-color: rgba(255, 255, 255, 0.4);
  background: rgba(255, 255, 255, 0.05);
}
.btn-secondary:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}
.btn-icon {
  background: transparent;
  border: none;
  color: rgba(255, 255, 255, 0.4);
  cursor: pointer;
  padding: 4px 8px;
  border-radius: 6px;
  font-size: 14px;
  transition: all 0.15s;
}
.btn-icon:hover {
  color: #f3bc2c;
  background: rgba(243, 188, 44, 0.1);
}
.btn-danger-ghost:hover {
  color: #f87171 !important;
  background: rgba(239, 68, 68, 0.1) !important;
}

/* ── Modal ── */
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.75);
  backdrop-filter: blur(4px);
  z-index: 100;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
}
.modal-panel {
  background: #0f1520;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 16px;
  display: flex;
  flex-direction: column;
  max-height: 90vh;
  width: 100%;
}
.modal-mediano {
  max-width: 580px;
}
.modal-detalle {
  max-width: 620px;
}
.modal-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  padding: 22px 24px 16px;
  flex-shrink: 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.07);
}
.modal-titulo {
  font-size: 17px;
  font-weight: 700;
  margin: 0;
  color: #f3bc2c;
}
.modal-sub {
  font-size: 12px;
  color: rgba(255, 255, 255, 0.4);
  margin: 4px 0 0;
}
.modal-footer {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 10px;
  padding: 16px 24px;
  flex-shrink: 0;
  border-top: 1px solid rgba(255, 255, 255, 0.07);
}

/* Form crear/editar */
.form-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 14px;
  padding: 20px 24px;
  overflow-y: auto;
  flex: 1;
}
.form-group {
  display: flex;
  flex-direction: column;
  gap: 5px;
}
.col-span-2 {
  grid-column: span 2;
}
label {
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: rgba(255, 255, 255, 0.45);
}
.label-hint {
  font-weight: 400;
  text-transform: none;
  opacity: 0.7;
}
.input-form {
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  color: #fff;
  padding: 9px 12px;
  font-size: 13px;
  outline: none;
  transition: border-color 0.15s;
  width: 100%;
  font-family: "Inter", sans-serif;
  box-sizing: border-box;
}
.input-form:focus {
  border-color: rgba(243, 188, 44, 0.5);
}
.input-form option {
  background: #0f1520;
}
textarea.input-form {
  resize: vertical;
}
.checks-row {
  flex-direction: row;
  gap: 20px;
  align-items: center;
  padding-top: 4px;
}
.check-label {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  font-size: 13px;
  color: rgba(255, 255, 255, 0.7);
}
.check-input {
  accent-color: #f3bc2c;
  width: 15px;
  height: 15px;
  cursor: pointer;
}

/* ── Detalle compra ── */
.detalle-compra-body {
  overflow-y: auto;
  flex: 1;
  padding: 20px 24px;
  display: flex;
  flex-direction: column;
  gap: 20px;
}
.compra-resumen-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0;
  border: 1px solid rgba(255, 255, 255, 0.07);
  border-radius: 10px;
  overflow: hidden;
}
.detalle-item {
  padding: 12px 16px;
  display: flex;
  flex-direction: column;
  gap: 4px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
  border-right: 1px solid rgba(255, 255, 255, 0.05);
}
.detalle-item:nth-child(even) {
  border-right: none;
}
.detalle-label {
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.07em;
  color: rgba(255, 255, 255, 0.35);
}
.detalle-val {
  font-size: 13px;
  color: #fff;
}

/* Asignaciones */
.asig-section {
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.asig-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
}
.asig-titulo {
  display: flex;
  align-items: center;
  gap: 7px;
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: #f3bc2c;
}
.asig-badge {
  font-size: 12px;
  color: rgba(255, 255, 255, 0.4);
}
.asig-vacio {
  font-size: 13px;
  color: rgba(255, 255, 255, 0.3);
  text-align: center;
  padding: 20px;
}
.asig-lista {
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.asig-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 14px;
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.06);
}
.asig-item.confirmada {
  border-color: rgba(74, 222, 128, 0.2);
  background: rgba(34, 197, 94, 0.05);
}
.asig-info {
  display: flex;
  flex-direction: column;
  gap: 2px;
}
.asig-nombre {
  font-size: 13px;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 6px;
}
.asig-email {
  font-size: 11px;
  color: rgba(255, 255, 255, 0.4);
}
.badge-menor {
  background: rgba(251, 146, 60, 0.15);
  color: #fb923c;
  border-radius: 10px;
  padding: 1px 8px;
  font-size: 10px;
  font-weight: 600;
}
.estado-confirmado {
  color: #4ade80;
  font-size: 12px;
  display: flex;
  align-items: center;
  gap: 4px;
}
.estado-pendiente {
  color: rgba(255, 255, 255, 0.3);
  font-size: 12px;
  display: flex;
  align-items: center;
  gap: 4px;
}

/* ── Beneficios en card ── */
.beneficios-list {
  display: flex;
  flex-wrap: wrap;
  gap: 5px;
  margin-top: 2px;
}
.badge-beneficio {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  background: rgba(34, 197, 94, 0.08);
  border: 1px solid rgba(74, 222, 128, 0.2);
  border-radius: 20px;
  padding: 2px 9px;
  font-size: 11px;
  color: #4ade80;
}
.badge-beneficio i {
  font-size: 10px;
}

/* ── Beneficios en modal ── */
.beneficios-loading,
.beneficios-vacio {
  font-size: 13px;
  color: rgba(255, 255, 255, 0.35);
  padding: 8px 0;
  display: flex;
  align-items: center;
  gap: 8px;
}
.btn-link {
  background: transparent;
  border: none;
  color: #f3bc2c;
  font-size: 13px;
  cursor: pointer;
  padding: 0;
  text-decoration: underline;
}
.beneficios-checks {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 6px;
  padding: 10px;
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 8px;
  max-height: 180px;
  overflow-y: auto;
}
.beneficio-check-label {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  font-size: 13px;
  color: rgba(255, 255, 255, 0.75);
  cursor: pointer;
  padding: 4px 6px;
  border-radius: 6px;
  transition: background 0.12s;
}
.beneficio-check-label:hover {
  background: rgba(255, 255, 255, 0.05);
}
.nuevo-beneficio-row {
  display: flex;
  gap: 6px;
  margin-top: 8px;
}
.input-nuevo-ben {
  flex: 1;
}
.btn-add-ben {
  background: rgba(243, 188, 44, 0.12);
  border: 1px solid rgba(243, 188, 44, 0.3);
  border-radius: 8px;
  color: #f3bc2c;
  padding: 0 12px;
  font-size: 16px;
  cursor: pointer;
  transition: all 0.15s;
  flex-shrink: 0;
}
.btn-add-ben:hover:not(:disabled) {
  background: rgba(243, 188, 44, 0.22);
}
.btn-add-ben:disabled {
  opacity: 0.35;
  cursor: not-allowed;
}

/* ── Responsive ── */
@media (max-width: 1024px) {
  .resumen-grid {
    grid-template-columns: 1fr 1fr;
  }
}
@media (max-width: 640px) {
  .crud-boletos {
    padding: 16px;
  }
  .resumen-grid {
    grid-template-columns: 1fr;
  }
  .tipos-grid {
    grid-template-columns: 1fr;
  }
  .form-grid {
    grid-template-columns: 1fr;
  }
  .col-span-2 {
    grid-column: auto;
  }
  .page-header {
    flex-direction: column;
    align-items: stretch;
  }
  .page-header .btn-primary {
    width: 100%;
    justify-content: center;
  }
  .filtros-bar {
    flex-direction: column;
    align-items: stretch;
  }
  .filtros-bar .search-wrap {
    max-width: none;
  }
  .modal-overlay {
    padding: 12px;
  }
}

/* ── Boletos físicos ────────────────────────────────────────────────────── */
.fisicos-intro {
  color: rgba(255, 255, 255, 0.55);
  font-size: 0.9rem;
  margin: 0 0 1.25rem;
  max-width: 720px;
}
.btn-generar-fisicos {
  width: 100%;
  justify-content: center;
  margin-top: 0.75rem;
}
.lotes-section {
  margin-top: 2rem;
}
.lotes-titulo {
  font-size: 1rem;
  font-weight: 600;
  color: #fff;
  margin: 0 0 0.75rem;
}
.preview-rango {
  background: rgba(243, 188, 44, 0.08);
  border: 1px solid rgba(243, 188, 44, 0.25);
  border-radius: 8px;
  padding: 10px 14px;
  font-size: 0.9rem;
  color: rgba(255, 255, 255, 0.85);
  margin: 0;
}
.preview-error {
  color: #f87171;
  font-size: 0.85rem;
  margin: 0.5rem 0 0;
}
.badge-fisico {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: rgba(148, 163, 184, 0.15);
  border: 1px solid rgba(148, 163, 184, 0.3);
  border-radius: 20px;
  padding: 4px 12px;
  font-size: 0.78rem;
  color: rgba(255, 255, 255, 0.7);
}
</style>
